import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from '@emotion/styled';
import { Timestamp } from '@britishcouncil/common-core';

import { Permission } from 'api';
import { FormWrapper, FormWrapperHandle } from 'components';
import { FollowUpForm } from './FollowUpForm';
import { FollowUpFormData, followUpValidation } from './followUpValidation';

type FollowUpSectionProps<TDetails extends object> = {
  onSaved?: () => void;
  onCreateCanceled?: () => void;
  followUpData?: TDetails | null;
  deleteFollowUp: (details: TDetails) => void;
  createFollowUp: (details: FollowUpFormData, callback?: () => void) => void;
  updateFollowUp: (details: FollowUpFormData, callback?: () => void) => void;
  isLoading?: boolean;
  isCreating?: boolean;
  isUpdating?: boolean;
  isDeleting?: boolean;
  createError?: unknown;
  updateError?: unknown;
};

export const FollowUpSection = <TDetails extends object>({
  onSaved,
  onCreateCanceled,
  followUpData,
  deleteFollowUp,
  createFollowUp,
  updateFollowUp,
  isLoading,
  isCreating,
  isUpdating,
  isDeleting,
  createError,
  updateError,
}: FollowUpSectionProps<TDetails>) => {
  const formWrapperRef = useRef<FormWrapperHandle>(null);

  const formMethods = useForm<FollowUpFormData>({
    mode: 'all',
    resolver: yupResolver(followUpValidation),
    context: { isSpeakingMonitoring: true },
    defaultValues: {
      followUpDate: Timestamp.toISODate(Timestamp.now()),
      isFromIdp: false,
      trainerId: null,
      idpTrainer: null,
    },
  });

  const { reset } = formMethods;

  useEffect(() => {
    if (followUpData) {
      reset(followUpData);
    }
  }, [followUpData, reset]);

  const handleSave = (data: FollowUpFormData) => {
    if (followUpData) {
      updateFollowUp(data, () => {
        formWrapperRef.current?.setMode('read');
        onSaved?.();
      });
    } else {
      createFollowUp(data, () => {
        formWrapperRef.current?.setMode('read');
        onSaved?.();
      });
    }
  };

  const handleDelete = () => {
    if (!followUpData) return;

    deleteFollowUp(followUpData);
  };

  if (isLoading) return null;

  return (
    <S.FollowUpContainer>
      <FormWrapper
        ref={formWrapperRef}
        defaultMode={followUpData ? 'read' : 'edit'}
        formMethods={formMethods}
        onDelete={handleDelete}
        isDeleting={isDeleting}
        onSubmit={handleSave}
        isSaving={isCreating || isUpdating}
        onCancel={() => {
          if (followUpData) {
            reset(followUpData);
          } else {
            onCreateCanceled?.();
          }
        }}
        error={createError || updateError}
        deletePermissions={[Permission.CompleteFollowUp]}
        editPermissions={[Permission.CompleteFollowUp]}
      >
        <FollowUpForm />
      </FormWrapper>
    </S.FollowUpContainer>
  );
};

const S = {
  FollowUpContainer: styled.div`
    margin-top: 2rem;
  `,
};
